import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calculator, BookOpen } from "lucide-react";
import { BrandMark, BrandWordmark } from "./LandingPage";
import SiteFootnote from "../components/SiteFootnote";
import Breadcrumb from "../components/Breadcrumb";
import ValuationCalculator from "../components/valuation/ValuationCalculator";
import { DATASET_VERSION } from "../lib/valuationMultiples";
import {
  setStandardMeta,
  upsertJsonLd,
  buildOrganizationSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
  ORG,
} from "../lib/seo";

/**
 * /valuation — the generic (industry-agnostic) business valuation calculator.
 * Serves users who arrive from broad search terms like "business valuation
 * calculator" and want to pick an industry before running. Industry-specific
 * landing pages live at /valuation/:industry with hand-authored content.
 */
export default function ValuationPage() {
  useEffect(() => {
    const url = `${ORG.url}/valuation`;
    const title =
      "Business Valuation Calculator — Free CFO-Grade Estimate | CFO Grade";
    const description =
      "How much is your business worth? Free valuation calculator using real public transaction data (Damodaran, Jan 2026) and private sale multiples (BizBuySell Q4 2025). 11 industries. No email required.";
    setStandardMeta({ title, description, url });
    const faq = valuationFaq();
    upsertJsonLd("valuation-graph", {
      "@context": "https://schema.org",
      "@graph": [
        buildOrganizationSchema(),
        buildBreadcrumbSchema([
          { name: "Home", url: ORG.url },
          { name: "Valuation", url },
        ]),
        buildFaqSchema(faq),
        {
          "@type": "WebApplication",
          "@id": `${url}#tool`,
          name: "CFO Grade Business Valuation Calculator",
          url,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          description,
          publisher: { "@id": `${ORG.url}#org` },
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
        },
      ],
    });
    return () => {
      const el = document.getElementById("valuation-graph");
      if (el) el.remove();
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-[#F7F5F0] bg-grain"
      data-testid="valuation-page"
    >
      <header className="max-w-4xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandWordmark size="sm" />
        </Link>
        <Link
          to="/"
          className="text-sm text-[#5C665D] hover:text-[#1F382E] inline-flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-10 pt-6 pb-20">
        <Breadcrumb
          items={[
            { name: "Home", to: "/" },
            { name: "Valuation", to: "/valuation" },
          ]}
        />

        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C665D] bg-white border border-[#E5E0D8] rounded-full px-3 py-1.5 mt-4">
          <Calculator size={13} strokeWidth={1.5} /> Free business valuation
        </div>

        <h1
          className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1F382E] leading-[1.05] tracking-tight mt-5"
          data-testid="valuation-title"
        >
          What&apos;s your business{" "}
          <span className="italic text-[#C26D5C]">actually worth?</span>
        </h1>
        <p className="text-base sm:text-lg text-[#1A1A1A] leading-relaxed mt-5 max-w-2xl">
          A CFO-grade estimate using real transaction data — Damodaran industry
          multiples for public comps, BizBuySell reports for private-market
          multiples, and standard size-discount methodology. Every number on
          the results page cites its source. No email required.
        </p>

        <ValuationCalculator
          initialIndustry="restaurants"
          navigateOnIndustryChange
        />

        {/* Methodology (visible below the fold for SEO) */}
        <section className="mt-16" data-testid="valuation-methodology">
          <div className="flex items-center gap-2">
            <BookOpen size={16} strokeWidth={1.5} className="text-[#5C665D]" />
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
              Methodology
            </p>
          </div>
          <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight mt-2">
            Where the multiples come from
          </h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            {[
              {
                n: "01",
                title: "Damodaran (NYU Stern)",
                body: "Free public dataset of EV/EBITDA and EV/Revenue medians for public companies, grouped by industry. Updated every January. Cited in every serious business-valuation textbook.",
              },
              {
                n: "02",
                title: "BizBuySell Insight Reports",
                body: "Quarterly report of actual small-business sales — median asking price, cash-flow multiple, and revenue multiple by category. The most representative source for main-street private transactions.",
              },
              {
                n: "03",
                title: "Duff & Phelps size discount",
                body: "Publicly-listed companies enjoy a size premium; private and small businesses do not. We haircut public-comp multiples by 10–65% based on your revenue bucket per the standard Duff & Phelps methodology.",
              },
              {
                n: "04",
                title: "Peer submissions",
                body: "As anonymous CFO Grade submissions accumulate, we cross-check the baseline multiples against implied medians from real businesses in your industry and size band.",
              },
            ].map((c) => (
              <div
                key={c.n}
                className="bg-white border border-[#E5E0D8] rounded-xl p-5"
              >
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#C26D5C]">
                  {c.n}
                </p>
                <p className="font-serif-display text-lg text-[#1F382E] tracking-tight mt-1">
                  {c.title}
                </p>
                <p className="text-[13.5px] text-[#1A1A1A] leading-relaxed mt-2">
                  {c.body}
                </p>
              </div>
            ))}
          </div>
          <p className="text-[12.5px] text-[#5C665D] mt-6 leading-relaxed max-w-2xl">
            Dataset version {DATASET_VERSION}. Multiples are refreshed each
            January (Damodaran) and quarterly (BizBuySell). This estimate is
            educational — a real transaction depends on buyer demand, deal
            terms, seller financing, and diligence findings. Not a formal
            appraisal.
          </p>
        </section>

        <section className="mt-14" data-testid="valuation-faq">
          <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight">
            Common questions
          </h2>
          <div className="mt-5 divide-y divide-[#E5E0D8] border-y border-[#E5E0D8]">
            {valuationFaq().map((qa, i) => (
              <details
                key={i}
                className="group py-4"
                data-testid={`valuation-faq-${i}`}
                {...(i === 0 ? { open: true } : {})}
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-[15px] font-medium text-[#1F382E] hover:text-[#C26D5C] transition-colors">
                  <span>{qa.q}</span>
                  <span className="text-[#5C665D] group-open:rotate-45 transition-transform text-lg leading-none">
                    +
                  </span>
                </summary>
                <p className="text-[14.5px] text-[#1A1A1A] leading-relaxed mt-3 pr-6">
                  {qa.a}
                </p>
              </details>
            ))}
          </div>
        </section>
      </main>

      <SiteFootnote />
    </div>
  );
}

function valuationFaq() {
  return [
    {
      q: "How accurate is a free business valuation calculator?",
      a: "This calculator gets you within roughly ±20% of a formal broker or CPA appraisal for most healthy small businesses. It uses the same underlying methodology (public-comp multiples adjusted for size, and private-transaction multiples from actual sales) that a $2,000–$5,000 valuation report uses. It won't capture qualitative factors like customer concentration, key-person risk, or lease/contract terms — a formal appraisal will.",
    },
    {
      q: "Why does the estimate span such a wide range?",
      a: "A real business doesn't sell for one number — it sells within a band. Different methods (SDE multiple, EBITDA multiple, revenue multiple, DCF) each capture different aspects of value. Showing the range is more honest than picking a single number. Where in the range you actually land depends on the buyer, deal structure, and diligence.",
    },
    {
      q: "What's the difference between EBITDA and SDE?",
      a: "SDE (Seller's Discretionary Earnings) is EBITDA plus the owner's compensation and discretionary personal expenses run through the business. For most small owner-operated businesses, buyers underwrite on SDE. For larger businesses with professional management, buyers use EBITDA. This calculator approximates SDE ≈ EBITDA — a formal appraisal would adjust further.",
    },
    {
      q: "Why apply a size discount to the Damodaran multiples?",
      a: "Damodaran's dataset is drawn from publicly-listed companies, which enjoy liquidity, scale, brand, and management depth that small private businesses don't. Duff & Phelps (and every serious appraiser) apply a size premium/discount to correct for this. A single-location restaurant simply does not trade at the same multiple as Chipotle.",
    },
    {
      q: "Does the CFO Grade affect the valuation?",
      a: "Not directly in this calculator — we keep valuation and financial-health scoring separate so each number is defensible on its own. In practice, though, buyers pay more for well-run businesses. An A-graded business typically sells at the top of its industry range (or above it); a D-graded one at a discount. Running your CFO Grade after this tells you which end of the range you're anchored to.",
    },
    {
      q: "How often are the multiples updated?",
      a: `Damodaran publishes his industry dataset each January; BizBuySell publishes quarterly. We refresh this calculator on the same cadence. You're currently viewing dataset ${DATASET_VERSION}.`,
    },
  ];
}

void BrandMark;
